import React, { useRef } from 'react';
import { FaPenFancy, FaMagic, FaRocket, FaFileImport, FaMousePointer, FaCogs, FaShareAlt } from 'react-icons/fa';
import RoughBorder from '../../kits/LayoutKit/RoughBorder';

/**
 * Wiederverwendbare ZenPost Workflow-Section Komponente
 * Zeigt den 3-Schritte Workflow: Schreiben → Transformieren → Publizieren
 */
const ZenPostWorkflowSection = ({
  title = "Dein neuer Workflow",
  subtitle = "Von der Idee zur globalen Publikation in Minuten, nicht Stunden.",
  steps = null,
  variant = "cards"
}) => {
  const workflowCard1Ref = useRef();
  const workflowCard2Ref = useRef();
  const workflowCard3Ref = useRef();

  // Default Workflow Steps
  const defaultSteps = [
    {
      icon: <FaPenFancy className="text-brand" />,
      
      title: "1. Schreiben",
      description: "Schreibe deinen Content einmal in Markdown oder EditorJS. Mit KI-Unterstützung.",
      features: [
        "KI-Textverbesserung",
        "Grammatik & Stil",
        "Content-Ideen"
      ],
      bgColor: "transparent",
      borderColor: "border-brand/20",
      hoverBorderColor: "hover:border-brand",
      ref: workflowCard1Ref
    },
    {
      icon: <FaMagic className="text-purple-600" />,
     
      title: "2. Transformieren",
      description: "Passe Tonalität, Länge und Zielgruppe mit lokaler KI an.",
      features: [
        "Professional → Casual",
        "Lang → Twitter-Thread",
        "Tech → Business"
      ],
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      hoverBorderColor: "hover:border-purple-400",
      ref: workflowCard2Ref
    },
    {
      icon: <FaRocket className="text-green-600" />,
      
      title: "3. Publizieren",
      description: "Ein Klick. 9+ Plattformen. Automatisch formatiert.",
      features: [
        "LinkedIn Post",
        "dev.to Artikel",
        "Twitter Thread"
      ],
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      hoverBorderColor: "hover:border-green-400",
      ref: workflowCard3Ref
    }
  ];

  const workflowSteps = steps || defaultSteps;

  if (variant === "timeline") {
    return (
      <section className="py-16 sm:py-20 px-4 text-brand">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-14 px-4">
            <div className="inline-flex items-center rounded-xl border border-brand  px-3 py-1 text-xs  text-[#EFEBDC] ">
              How it works - Content AI Studio
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold">
              {title}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#EFEBDC] max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-0 right-0 top-7 h-px bg-gradient-to-r from-brand via-amber-500/30 to-amber-200/30" />
            <div className="grid gap-8 md:grid-cols-4">
              {workflowSteps.map((step, index) => (
                <div key={index} className="relative text-center md:text-left">
                  <div className="mx-auto md:mx-0 flex h-14 w-14 items-center justify-center rounded-full bg-[#191919] text-lg font-bold text-brand border border-brand shadow-sm shadow-slate-800">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="mt-5 text-lg sm:text-xl font-semibold text-brand">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-[#EFEBDC]">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 sm:py-20 px-4 bg-slate-950 text-white overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute right-0 top-0 h-48 w-48 bg-gradient-to-br from-brand/40 to-transparent opacity-60 blur-3xl" />
        <div className="absolute left-[-60px] bottom-0 h-64 w-64 bg-gradient-to-br from-violet-500/60 to-transparent opacity-30 blur-[120px]" />
        <div className="absolute inset-x-0 top-1/2 h-64 bg-[radial-gradient(circle_at_top,_rgba(215,172,116,0.45),_transparent_60%)] opacity-40 blur-sm" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-14 px-4 space-y-3">
          <span className="text-xs uppercase tracking-[0.4em] text-brand/70 font-mono">ZenFlow</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">{title}</h2>
          <p className="text-base sm:text-lg text-slate-300 max-w-3xl mx-auto">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {workflowSteps.map((step, index) => (
            <div key={step.title} className="relative">
              <div
                ref={step.ref}
                className={`relative h-full rounded-[32px] border border-white/10 bg-slate-900/70 p-6 sm:p-8 overflow-hidden transition duration-500 ${step.hoverBorderColor}`}
              >
                <div className={`absolute inset-0 rounded-[32px] ${step.bgColor}`} aria-hidden="true" />
                <div className="relative z-10 flex flex-col h-full gap-4">
                  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-slate-900/80 border border-brand/40 mx-auto">
                    {step.icon}
                  </div>
                  <h3 className="text-2xl font-semibold text-white">{step.title}</h3>
                  <p className="text-sm text-slate-200 leading-relaxed">{step.description}</p>
                  <div className="mt-auto space-y-2 text-xs text-slate-300">
                    {step.features.map((feature, featureIndex) => (
                      <div key={`${step.title}-${featureIndex}`} className="flex items-start gap-2">
                        <span className="flex h-3 w-3 mt-1 rounded-full border border-brand/60" />
                        <span className="leading-tight text-slate-200">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <RoughBorder
                  parentRef={step.ref}
                  radius={28}
                  padding={4}
                  color="#AC8E66"
                  strokeWidth={0.35}
                  roughness={1.2}
                  bowing={1.5}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ZenPostWorkflowSection;
